import { useState } from 'react'
import { useApp } from '../context/AppContext'
import { persistDeletedVersion } from '../lib/lumiAppendOnly'
import { formatCurrency, formatDate, nowISO } from '../utils'
import type { Pagamento } from '../types'

const FORMAS = ['PIX', 'TED', 'BOLETO', 'DINHEIRO', 'OUTRO'] as const

export default function Pagamentos() {
  const { pagamentos, setPagamentos, contasPagar, setContasPagar, fornecedores, obras, addLog, itensOrdemCompra, setItensOrcados } = useApp()
  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [filtroObra, setFiltroObra] = useState('')
  const [filtroForma, setFiltroForma] = useState('')
  const [erro, setErro] = useState('')
  const [form, setForm] = useState({
    conta_pagar_id: '',
    data_pagamento: '',
    valor_pago: '',
    forma_pagamento: 'PIX' as Pagamento['forma_pagamento'],
    numero_documento: '',
    banco: '',
    observacoes: '',
  })

  const contasAbertas = contasPagar.filter(c => c.status !== 'PAGO' && c.status !== 'CANCELADO')
  const contasSelecionaveis = editId
    ? contasPagar.filter(c => c.status !== 'CANCELADO')
    : contasAbertas

  const pagamentosFiltrados = pagamentos.filter(p => {
    const conta = contasPagar.find(c => c.id === p.conta_pagar_id)
    if (filtroObra && conta?.obra_id !== filtroObra) return false
    if (filtroForma && p.forma_pagamento !== filtroForma) return false
    return true
  })

  function openNew() {
    setEditId(null)
    setErro('')
    setForm({ conta_pagar_id: '', data_pagamento: '', valor_pago: '', forma_pagamento: 'PIX', numero_documento: '', banco: '', observacoes: '' })
    setShowModal(true)
  }

  function openEdit(p: Pagamento) {
    setEditId(p.id)
    setErro('')
    setForm({
      conta_pagar_id: p.conta_pagar_id,
      data_pagamento: p.data_pagamento,
      valor_pago: String(p.valor_pago),
      forma_pagamento: p.forma_pagamento,
      numero_documento: p.numero_documento || '',
      banco: p.banco || '',
      observacoes: p.observacoes || '',
    })
    setShowModal(true)
  }

  function salvar() {
    setErro('')
    if (!form.conta_pagar_id) { setErro('Selecione a conta a pagar.'); return }
    if (!form.data_pagamento) { setErro('Informe a data do pagamento.'); return }
    const valor = parseFloat(form.valor_pago)
    if (!valor || valor <= 0) { setErro('Informe um valor válido.'); return }

    const conta = contasPagar.find(c => c.id === form.conta_pagar_id)
    if (!conta) { setErro('Conta a pagar não encontrada.'); return }

    const jaJaPago = pagamentos
      .filter(p => p.conta_pagar_id === form.conta_pagar_id && p.id !== editId)
      .reduce((acc, p) => acc + p.valor_pago, 0)
    const saldoAberto = conta.valor_previsto - jaJaPago
    if (valor > saldoAberto + 0.01) {
      setErro(`Valor excede saldo aberto da conta (${formatCurrency(saldoAberto)}). Edite a conta antes de prosseguir.`)
      return
    }

    const valorAnterior = editId ? pagamentos.find(p => p.id === editId)?.valor_pago || 0 : 0
    const deltaPagamento = valor - valorAnterior
    const nextPagamentos = editId
      ? pagamentos.map(p => p.id === editId ? {
        ...p,
        conta_pagar_id: form.conta_pagar_id,
        data_pagamento: form.data_pagamento,
        valor_pago: valor,
        forma_pagamento: form.forma_pagamento,
        numero_documento: form.numero_documento,
        banco: form.banco,
        observacoes: form.observacoes,
      } : p)
      : [{
        id: 'pag' + Date.now(),
        conta_pagar_id: form.conta_pagar_id,
        data_pagamento: form.data_pagamento,
        valor_pago: valor,
        forma_pagamento: form.forma_pagamento,
        numero_documento: form.numero_documento,
        banco: form.banco,
        observacoes: form.observacoes,
        createdAt: nowISO(),
      }, ...pagamentos]

    if (editId) {
      setPagamentos(nextPagamentos)
      addLog('PAGAMENTO', editId, 'EDITADO', `Pagamento editado: ${formatCurrency(valor)}`)
    } else {
      setPagamentos(nextPagamentos)
      addLog('PAGAMENTO', nextPagamentos[0].id, 'PAGO', `Pagamento ${form.forma_pagamento} ${formatCurrency(valor)} registrado`)
    }

    const totalPagoConta = nextPagamentos
      .filter(p => p.conta_pagar_id === form.conta_pagar_id)
      .reduce((acc, p) => acc + p.valor_pago, 0)
    setContasPagar(prev => prev.map(c => {
      if (c.id !== form.conta_pagar_id) return c
      const status = totalPagoConta >= c.valor_previsto - 0.01 ? 'PAGO' : totalPagoConta > 0 ? 'PARCIALMENTE_PAGO' : c.status
      return { ...c, valor_pago: totalPagoConta, status }
    }))

    if (conta.ordem_compra_id && Math.abs(deltaPagamento) > 0.01) {
      const itensOC = itensOrdemCompra.filter(i => i.ordem_compra_id === conta.ordem_compra_id)
      const totalOC = itensOC.reduce((acc, item) => acc + item.valor_total, 0)
      setItensOrcados(prev => prev.map(item => {
        const valorDoItem = itensOC.filter(i => i.item_orcado_id === item.id).reduce((acc, i) => acc + i.valor_total, 0)
        if (!valorDoItem || !totalOC) return item
        const parcela = deltaPagamento * (valorDoItem / totalOC)
        const valorPago = Math.max(0, item.valor_pago + parcela)
        return { ...item, valor_pago: valorPago, saldo_atual: item.valor_total_previsto - item.valor_comprometido - valorPago }
      }))
    }
    setShowModal(false)
  }

  function aplicarDeltaOrcamento(contaId: string, deltaPagamento: number) {
    const conta = contasPagar.find(c => c.id === contaId)
    if (!conta?.ordem_compra_id || Math.abs(deltaPagamento) <= 0.01) return

    const itensOC = itensOrdemCompra.filter(i => i.ordem_compra_id === conta.ordem_compra_id)
    const totalOC = itensOC.reduce((acc, item) => acc + item.valor_total, 0)
    setItensOrcados(prev => prev.map(item => {
      const valorDoItem = itensOC.filter(i => i.item_orcado_id === item.id).reduce((acc, i) => acc + i.valor_total, 0)
      if (!valorDoItem || !totalOC) return item
      const parcela = deltaPagamento * (valorDoItem / totalOC)
      const valorPago = Math.max(0, item.valor_pago + parcela)
      return { ...item, valor_pago: valorPago, saldo_atual: item.valor_total_previsto - item.valor_comprometido - valorPago }
    }))
  }

  function recalcularConta(contaId: string, nextPagamentos: Pagamento[]) {
    setContasPagar(prev => prev.map(c => {
      if (c.id !== contaId) return c
      const totalPagoConta = nextPagamentos
        .filter(p => p.conta_pagar_id === contaId)
        .reduce((acc, p) => acc + p.valor_pago, 0)
      const status = totalPagoConta >= c.valor_previsto - 0.01 ? 'PAGO' : totalPagoConta > 0 ? 'PARCIALMENTE_PAGO' : 'PENDENTE'
      return { ...c, valor_pago: totalPagoConta, status }
    }))
  }

  async function deletePagamento(pagamento: Pagamento) {
    const confirmed = window.confirm(`Excluir o pagamento de ${formatCurrency(pagamento.valor_pago)}?`)
    if (!confirmed) return

    try {
      await persistDeletedVersion('pagamentos', pagamento as unknown as Record<string, unknown>)
    } catch {
      setErro('Erro, tente novamente mais tarde.')
      return
    }
    const nextPagamentos = pagamentos.filter(item => item.id !== pagamento.id)
    setPagamentos(nextPagamentos)
    recalcularConta(pagamento.conta_pagar_id, nextPagamentos)
    aplicarDeltaOrcamento(pagamento.conta_pagar_id, -pagamento.valor_pago)
    addLog('PAGAMENTO', pagamento.id, 'CANCELADO', `Pagamento excluido: ${formatCurrency(pagamento.valor_pago)}`)
  }

  const totalPago = pagamentosFiltrados.reduce((a, p) => a + p.valor_pago, 0)

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-white">Pagamentos</h1>
          <p className="text-xs text-gray-500 mt-0.5">Registro de baixas financeiras</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={openNew}>
          <span className="text-base leading-none">+</span> Registrar Pagamento
        </button>
      </div>

      {/* Filtros */}
      <div className="flex gap-3 mb-4">
        <select className="input-field w-44" value={filtroObra} onChange={e => setFiltroObra(e.target.value)}>
          <option value="">Todas as obras</option>
          {obras.map(o => <option key={o.id} value={o.id}>{o.nome_obra}</option>)}
        </select>
        <select className="input-field w-36" value={filtroForma} onChange={e => setFiltroForma(e.target.value)}>
          <option value="">Todas as formas</option>
          {FORMAS.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
      </div>

      {/* Totalizador */}
      <div className="card p-3 mb-4 flex items-center gap-6">
        <div>
          <span className="label-field">Total pago (filtro)</span>
          <span className="text-white font-semibold">{formatCurrency(totalPago)}</span>
        </div>
        <div>
          <span className="label-field">Registros</span>
          <span className="text-white font-semibold">{pagamentosFiltrados.length}</span>
        </div>
      </div>

      {/* Tabela */}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/[0.06]">
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Conta / Título</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Data</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Forma</th>
              <th className="text-right px-4 py-3 text-xs text-gray-500 font-medium">Valor Pago</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Doc / Banco</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {pagamentosFiltrados.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-600 text-sm">
                  Nenhum pagamento registrado.
                </td>
              </tr>
            ) : pagamentosFiltrados.map(p => {
              const conta = contasPagar.find(c => c.id === p.conta_pagar_id)
              const fornecedor = fornecedores.find(f => f.id === conta?.fornecedor_id)
              return (
                <tr key={p.id} className="table-row-hover border-b border-white/[0.04]">
                  <td className="px-4 py-3">
                    <div className="text-white text-xs font-medium">{conta?.numero_titulo || '-'}</div>
                    <div className="text-gray-500 text-xs">{fornecedor?.nome_fantasia || '-'}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-300 text-xs">{formatDate(p.data_pagamento)}</td>
                  <td className="px-4 py-3">
                    <span className={`status-badge ${
                      p.forma_pagamento === 'PIX' ? 'bg-cyan-500/10 text-cyan-400' :
                      p.forma_pagamento === 'TED' ? 'bg-blue-500/10 text-blue-400' :
                      p.forma_pagamento === 'BOLETO' ? 'bg-yellow-500/10 text-yellow-400' :
                      'bg-gray-500/10 text-gray-400'
                    }`}>{p.forma_pagamento}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-emerald-400 font-semibold text-xs">{formatCurrency(p.valor_pago)}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{p.numero_documento || '-'} {p.banco ? `/ ${p.banco}` : ''}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button className="text-xs text-gray-500 hover:text-white transition-colors" onClick={() => openEdit(p)}>editar</button>
                      <button className="text-xs text-red-400 hover:text-red-300 transition-colors" onClick={() => deletePagamento(p)}>excluir</button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay fixed inset-0 flex items-center justify-center z-50">
          <div className="card w-full max-w-md p-6 mx-4">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-sm font-semibold text-white">{editId ? 'Editar Pagamento' : 'Registrar Pagamento'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-white text-lg leading-none">×</button>
            </div>
            {erro && <div className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded p-2 mb-4">{erro}</div>}
            <div className="space-y-3">
              <div>
                <label className="label-field">Conta a Pagar *</label>
                <select className="input-field" value={form.conta_pagar_id} onChange={e => setForm(f => ({ ...f, conta_pagar_id: e.target.value }))}>
                  <option value="">Selecione...</option>
                  {contasSelecionaveis.map(c => {
                    const forn = fornecedores.find(f => f.id === c.fornecedor_id)
                    const saldo = c.valor_previsto - pagamentos.filter(p => p.conta_pagar_id === c.id).reduce((a, p) => a + p.valor_pago, 0)
                    return <option key={c.id} value={c.id}>{c.numero_titulo} — {forn?.nome_fantasia || '-'} — Saldo: {formatCurrency(saldo)}</option>
                  })}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-field">Data Pagamento *</label>
                  <input type="date" className="input-field" value={form.data_pagamento} onChange={e => setForm(f => ({ ...f, data_pagamento: e.target.value }))} />
                </div>
                <div>
                  <label className="label-field">Valor Pago *</label>
                  <input type="number" step="0.01" className="input-field" placeholder="0,00" value={form.valor_pago} onChange={e => setForm(f => ({ ...f, valor_pago: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-field">Forma Pagamento</label>
                  <select className="input-field" value={form.forma_pagamento} onChange={e => setForm(f => ({ ...f, forma_pagamento: e.target.value as Pagamento['forma_pagamento'] }))}>
                    {FORMAS.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-field">Banco</label>
                  <input className="input-field" placeholder="Ex: Bradesco" value={form.banco} onChange={e => setForm(f => ({ ...f, banco: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="label-field">Nº Documento / Comprovante</label>
                <input className="input-field" placeholder="TED-001, Chave PIX..." value={form.numero_documento} onChange={e => setForm(f => ({ ...f, numero_documento: e.target.value }))} />
              </div>
              <div>
                <label className="label-field">Observações</label>
                <textarea className="input-field" rows={2} value={form.observacoes} onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button className="btn-ghost" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn-primary" onClick={salvar}>Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
